import { forwardRef } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { Link } from "react-router-dom";

const Footer = forwardRef<HTMLElement>(function Footer(_props, ref) {
  const { t, p } = useLanguage();

  return (
    <footer ref={ref} className="relative bg-card footer-brighten overflow-hidden">
      {/* Atmospheric bloom */}
      <div
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[120vw]"
        style={{
          background: `radial-gradient(ellipse 50% 60% at 50% 0%, hsl(var(--warm) / 0.04) 0%, transparent 70%)`,
        }}
        aria-hidden="true"
      />

      <div className="container relative z-10 mx-auto px-4 py-20 lg:px-8">
        {/* Top row */}
        <div className="mb-14 flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-sm font-semibold tracking-[0.2em] uppercase text-foreground">
              Frank Pistorius
            </h2>
            <p className="mt-1 text-xs text-muted-foreground/70">
              {t.footer.tagline}
            </p>
          </div>
          <div className="max-w-xs text-right">
            <p className="text-sm font-medium text-foreground">
              {t.footer.stayConnected}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t.footer.stayConnectedText}
            </p>
          </div>
        </div>

        {/* Columns */}
        <div className="grid gap-10 pt-12 sm:grid-cols-3">
          <div>
            <h3 className="mb-5 text-[11px] font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              {t.footer.forCustomers}
            </h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <Link to={p("products")} className="block transition-colors hover:text-foreground">{t.nav.products}</Link>
              <Link to={p("about")} className="block transition-colors hover:text-foreground">{t.nav.about}</Link>
              <Link to={p("contact")} className="block transition-colors hover:text-foreground">{t.nav.contact}</Link>
            </div>
          </div>

          <div>
            <h3 className="mb-5 text-[11px] font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              {t.footer.support}
            </h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <Link to={p("contact")} className="block transition-colors hover:text-foreground">{t.contact.contactUs}</Link>
              <a href="mailto:info@frankpistorius.de" className="block transition-colors hover:text-foreground">info@frankpistorius.de</a>
              <a href="tel:+491773078721" className="block transition-colors hover:text-foreground">+49 177 307 8721</a>
            </div>
          </div>

          <div>
            <h3 className="mb-5 text-[11px] font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              {t.footer.followUs}
            </h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="block transition-colors hover:text-foreground">Instagram</a>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="block transition-colors hover:text-foreground">Facebook</a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-14 flex flex-col gap-3 pt-8 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} Frank Pistorius. {t.footer.rights}</p>
          <div className="flex flex-wrap items-center gap-2">
            {([
              { key: "impressum" as const, label: t.footer.impressum },
              { key: "datenschutz" as const, label: t.footer.datenschutz },
              { key: "cookiePolicy" as const, label: t.footer.cookiePolicy },
              { key: "terms" as const, label: t.footer.terms },
            ]).map(({ key, label }) => (
              <Link
                key={key}
                to={p(key)}
                className="rounded-full border border-border/40 px-3 py-1 text-xs text-muted-foreground transition-all duration-200 hover:border-foreground/20 hover:text-foreground hover:bg-foreground/5"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
